"use client";

import {
  faHeart,
  faHourglass, faHourglassHalf,
  faUser,
} from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { LayoutProvider } from "@audacia-hq/shared/contexts";
import SharedComponents, { EventTrackerWrapper } from "@audacia-hq/shared/components";

import { useAnnouncements } from "../../context/AnnouncementContext";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useConsultation } from "../../context/ConsultationContext";
import { useExpertList } from "../../context/ExpertListContext";
import { useMember } from "../../context/MemberContext";
import { useQueue } from "../../context/QueueContext";
import { useSurveys } from "../../context/SurveyContext";
import Button from "../Button";
import VisitorSignup from "../consultations/VisitorSignup";
import CookieBanner from "../CookieBanner";
import Logo from "../Logo";
import Notifications from "../notifications/Notifications";
import MarketingBanner from "../packs/MarketingBanner";

import ExpertSearch from "./ExpertSearch";
import MemberCodeTime from "./MemberCodeTime";
import Menu from "./Menu";
import MenuButton from "./MenuButton";
import OngoingConsultationBanner from "./OngoingConsultationBanner";
import UserSlider from "./UserSlide";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserSlider, setShowUserSlider] = useState(false);
  const [livechatLayout, setLivechatLayout] = useState(false);
  const [hideCopyright, setHideCopyright] = useState(false);
  const { domain } = useAppContext();
  const { user, login, signup } = useAuth();
  const { modalSignup, setModalSignup } = useMember();
  const { memberQueues } = useQueue();
  const { clearFilters } = useExpertList();
  const { ongoingConsultation, ongoingTrialConsultation } = useConsultation();
  const path = usePathname();
  const params = useSearchParams();
  const footerHeight = 36;

  const { t } = useTranslation(["common", "survey"]);

  const { renderAnnounceBanner } = useAnnouncements();
  const { renderSurveyBanner, renderSurvey } = useSurveys();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [mainHeight, setMainHeight] = useState<number>(0);

  useEffect(() => {
    if (!headerRef?.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!headerRef?.current) return;
      setHeaderHeight(headerRef?.current?.offsetHeight);
      setMainHeight(entries[0].contentRect.height - headerRef.current.offsetHeight);
    });
    resizeObserver.observe(document.body);

    const mutationObserver = new MutationObserver((entries) => {
      setHeaderHeight((headerRef.current?.offsetHeight || 0));
      setMainHeight(window.innerHeight - (headerRef.current?.offsetHeight || 0));
    });
    mutationObserver.observe(headerRef.current, { childList: true, subtree: true });
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [headerRef.current]);

  useEffect(() => {
    setLivechatLayout(["LIVECHAT", "VIDEO"].includes(ongoingConsultation?.channel)
      || ["LIVECHAT", "VIDEO"].includes(ongoingTrialConsultation?.channel));
  }, [ongoingConsultation, ongoingTrialConsultation]);

  useEffect(() => {
    if (path === "/messages") {
      setHideCopyright(true);
    } else {
      setHideCopyright(false);
    }
  }, [path]);

  const getCopyright = () => {
    const year = new Date().getFullYear();
    if (domain.includes("idealvoyance")) {
      return `Ⓒ IdealVoyance ${year}`;
    }
    if (domain.includes("manticus")) {
      return `Ⓒ Manticus ${year}`;
    }
    return `Ⓒ IdealVoyance ${year}`;
  };

  return (
    <EventTrackerWrapper identifier="app_tracking">
      <div ref={headerRef} className="sticky top-0 z-50 bg-white">
        {!(ongoingConsultation || ongoingTrialConsultation) && renderSurveyBanner()}
        {renderSurvey()}
        <OngoingConsultationBanner consultation={ongoingConsultation} trialConsultation={ongoingTrialConsultation} />

        <div className={`${livechatLayout ? "hidden md:flex " : ""}wrapper px-6 flex justify-between items-center space-x-6 md:space-x-12 py-4`}>
          <Link href="/">
            <Logo domain={domain} className="w-[150px] xl:w-[200px]" />
          </Link>
          {user && <MemberCodeTime className="hidden md:flex" />}
          <div>
            <div className="flex gap-1 md:gap-3">
              {!user && (
                <>
                  <Button className="hidden sm:block" render="simple" size="small+" onClick={() => login()} data-pw="signInMenu">
                    {t("menu.login")}
                  </Button>
                  <Button render="primary" size="small+" onClick={() => signup()} data-pw="signUpMenu">
                    {t("menu.signup")}
                  </Button>
                </>
              )}
              {user && (
                <>
                  <Notifications />
                  <div className="mr-2 md:mr-6 hover" data-pw="queueMenu">
                    <Link href="/my-queues" className="relative">
                      {Object.keys(memberQueues).length > 0 ? (
                        <>
                          <FontAwesomeIcon icon={faHourglassHalf} className="w-8 fa-lg" />
                          <div className="absolute -right-[8px] -top-[8px] bg-red-500 rounded-lg w-[20px] h-[20px]
                          text-white text-xs flex items-center justify-center"
                          >
                            {Object.keys(memberQueues).length}
                          </div>
                        </>
                      ) : (
                        <FontAwesomeIcon icon={faHourglass} className="w-8 fa-lg" />
                      )}

                    </Link>
                  </div>
                  <div className="mr-2 md:mr-6 hover">
                    <Link
                      onClick={() => (params?.get("favorite") ? clearFilters() : clearFilters({ frontFilters: { expertType: ["FAVORITE"] } }))}
                      href={params?.get("favorite") ? "/" : "/?favorite=true"}
                    >
                      <FontAwesomeIcon icon={faHeart} className={`w-8 fa-lg ${params?.get("favorite") ? "text-red-500" : ""} `} />
                    </Link>
                  </div>
                  <button
                    type="button"
                    className="hover"
                    onClick={() => setShowUserSlider(true)}
                    data-pw="accountMenu"
                  >
                    <FontAwesomeIcon icon={faUser} className="w-8 fa-lg" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={`${livechatLayout ? "hidden md:block" : ""} w-full border-b border-slate-300`}>
          <div className="wrapper px-6 pb-4 flex justify-between gap-3">
            <div className="flex justify-between items-center">
              <MenuButton
                className="md:hidden place-content-center sm:place-content-start p-2 -ml-2"
                open={showMobileMenu}
                onChange={setShowMobileMenu}
              />
              <Menu className="hidden md:flex pt-2" />
            </div>
            <div className="w-full max-w-sm">
              <ExpertSearch />
            </div>
          </div>
        </div>
        {showMobileMenu && (
          <div className="bg-slate-200 py-5 md:hidden">
            <Menu
              className="flex flex-col space-y-3 text-xl pl-5"
              closeMenu={() => {
                setShowMobileMenu(false);
              }}
            />
          </div>
        )}
        <div className={`${(livechatLayout || ongoingConsultation || ongoingTrialConsultation) ? "hidden md:block" : ""}`}>
          {!path.includes("checkout") && <MarketingBanner />}
        </div>
      </div>
      {user && (
        <UserSlider
          open={showUserSlider}
          close={() => setShowUserSlider(false)}
        />
      )}

      <LayoutProvider footerHeight={footerHeight} headerHeight={headerHeight} mainHeight={mainHeight}>
        {!livechatLayout && renderAnnounceBanner("main")}
        {children}
      </LayoutProvider>

      <div className={`${(livechatLayout || hideCopyright) ? "hidden md:block" : ""}flex items-center bg-blue-950 py-2 px-6`} style={{ height: footerHeight }}>
        <div className="text-left text-xs text-white">{getCopyright()}</div>
      </div>
      <CookieBanner />
      <SharedComponents.Modal title={t("member:visitor.signup.modalTitle")} show={!user && modalSignup && !(path?.startsWith("/register") || path?.startsWith("/login"))} onClose={() => setModalSignup(false)}>
        <VisitorSignup />
      </SharedComponents.Modal>
    </EventTrackerWrapper>
  );
};

export default Layout;
